<template>
  <div v-if="pageResults.length" class="search-results">
    <ul class="search-results__list">
      <li v-for="result in pageResults" :key="result.id" class="search-results__item">
        <a v-if="result.thumbnailURL" :href="result.url" class="search-results__thumbnail d-none d-sm-block">
          <img :src="result.thumbnailURL" :alt="`Thumbnail for: ${result.title}`" />
        </a>
        <div class="search-results__details">
          <a
            class="search-results__details--title"
            :class="{ 'external-link': isExternalLink(result.url) }"
            :href="result.url"
            :target="isExternalLink(result.url) ? '_blank' : null"
            :rel="isExternalLink(result.url) ? 'noopener noreferrer' : null"
          >
            <h2
              :class="['search-results__title', 'type-' + result.type]"
            >
              <i :class="resultIcon(result.type).classes" aria-hidden="true">{{ resultIcon(result.type).icon || "" }}</i>
              {{ result.title }}
              <span
                v-if="isExternalLink(result.url)"
                class="material-icons no-style"
                role="image"
                aria-label="Opens in new tab"
              >
                open_in_new
              </span>
            </h2>
          </a>
          <p class="search-results__summary">{{ result.summary }}</p>
          <div class="d-flex">
            <a v-if="result.thumbnailURL" :href="result.url" class="search-results__thumbnail d-sm-none">
              <img :src="result.thumbnailURL" :alt="`Thumbnail for: ${result.title}`" />
            </a>
            <div class="search-results__meta">
              <a v-if="result.fileURL" :href="result.fileURL" class="search-results__url" target="_blank">
                Download now <span v-if="result.fileMetaData">{{ result.fileMetaData }}</span>
              </a>
              <a v-else :href="result.url" class="search-results__url">{{ result.url | addHost }}</a>
              <span v-if="result.dateString !== false" class="search-results__date">
                <i class="material-icons" aria-hidden="true">alarm</i>
                <template v-if="result.dateString">{{ result.dateString }}</template>
                <template v-else>Updated {{ result.date | dateFormat }}</template>
              </span>
            </div>
          </div>
        </div>
      </li>
    </ul>
    <nav aria-label="Search results pages">
      <ul class="pagination">
        <li v-if="currentPage > 1" class="pagination__prev">
          <a @click.prevent="currentPage--" href="#" class="btn btn-round btn-outline-primary btn-arrow-back">
            <i class="material-icons" aria-hidden="true">keyboard_arrow_left</i>
            Previous
          </a>
        </li>
        <li
          v-for="page in pageRange"
          :key="page"
          class="pagination__page"
          :class="{
            'pagination__page--active': currentPage == page,
            'pagination__page--first': page == 1,
            'pagination__page--last': page == pageCount,
          }"
        >
          <a
            @click.prevent="changePage(page)"
            href="#"
            class="btn btn-circle btn-outline-primary"
            :class="{ active: currentPage == page }"
            :aria-current="currentPage == page ? 'page' : null"
            >{{ page }}
          </a>
        </li>
        <li v-if="currentPage < pageCount" class="pagination__next">
          <a @click.prevent="currentPage++" href="#" class="btn btn-round btn-outline-primary btn-arrow">
            Next
            <i class="material-icons" aria-hidden="true">keyboard_arrow_right</i>
          </a>
        </li>
      </ul>
    </nav>
  </div>
  <h1 v-else-if="errorMessage" class="search-results__message search-results__message--error">{{ errorMessage }}</h1>
  <h1 v-else class="search-results__message">No results found</h1>
</template>

<script>
export default {
  props: {
    errorMessage: String,
    results: Array,
    resultsPerPage: {
      type: Number,
      default: 10,
    },
    paginationMax: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      currentPage: 1,
    }
  },
  computed: {
    pageResults: function() {
      const start = (this.currentPage - 1) * this.resultsPerPage
      return this.results.slice(start, start + this.resultsPerPage)
    },
    pageCount: function() {
      return Math.ceil(this.results.length / this.resultsPerPage)
    },
    pageRange: function() {
      let lowerLimit = this.currentPage - Math.floor(this.paginationMax / 2) - 1;
      let upperLimit = this.currentPage + Math.floor(this.paginationMax / 2);

      if (lowerLimit < 0) {
        lowerLimit = 0;
        upperLimit = this.paginationMax;
      }

      if (upperLimit > this.pageCount) {
        upperLimit = this.pageCount;
        lowerLimit = Math.max(0, upperLimit - this.paginationMax);
      }

      const pageButtons = [];

      for (let i = lowerLimit; i < upperLimit; i++) {
        pageButtons.push(i + 1);
      }

      return pageButtons;
    },
  },
  methods: {
    changePage(page) {
      if (this.currentPage != page) {
        this.currentPage = page
        window.scrollTo(0, 0)
      }
    },
    resultIcon(resultType) {
      switch (resultType) {
        case 'page':
          return {
            classes: ['material-icons'],
            icon: 'format_align_left',
          }

        case 'event':
          return {
            classes: ['icon-event'],
            icon: null,
          }

        case 'news':
          return {
            classes: ['icon-news'],
            icon: null,
          }

        case 'park':
          return {
            classes: ['icon-park'],
            icon: null,
          }

        case 'pdf':
          return {
            classes: ['material-icons'],
            icon: 'picture_as_pdf',
          }

        default:
          return {
            classes: ['material-icons'],
            icon: 'library_books',
          }
      }
    },
    isExternalLink(url) {
      if(url.startsWith("http")) {
        return true
      }
      return false
    },
  },
  filters: {
    addHost: url => {
      return url.startsWith("http") ? url : window.location.host + url
    },
    dateFormat: dateStr => {
      const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"]
      const date = new Date(dateStr)
      const day = (date.getDate() < 10 ? "0" : "") + date.getDate()

      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },
  },
}
</script>

<style lang="scss" scoped>
.search-results {
  &__list {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
  &__item {
    display: flex;
    &:not(:last-child) {
      margin-bottom: 20px;
    }
  }
  &__thumbnail {
    margin-right: 20px;
    max-width: 105px;
    flex-shrink: 0;
    img {
      max-width: 100%;
    }

    &:before {
      content: none;
    }
  }

  &__details {
    &--title {
      &:before {
        content: none;
      }
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
  }

  &__url {
    word-break: break-word;
  }
}
.pagination {
  list-style-type: none;
  margin-bottom: 0;
  margin-top: 20px;
  padding: 0;
  li {
    display: inline-block;
  }
}
</style>
